<template>
  <div v-if="visible" class="dialog-overlay" @click="handleOverlayClick">
    <div class="dialog" :class="customClass" :style="width ? { maxWidth: width } : {}" @click.stop>
      <div class="dialog-header">
        <div class="dialog-icon" :class="type">
          <span v-if="type === 'info'">ℹ️</span>
          <span v-else-if="type === 'warning'">⚠️</span>
          <span v-else-if="type === 'error'">❌</span>
          <span v-else-if="type === 'success'">✅</span>
        </div>
        <h3 class="dialog-title">{{ title }}</h3>
        <button v-if="closable" @click="close" class="dialog-close">×</button>
      </div>
      <div class="dialog-body">
        <p class="dialog-message">{{ message }}</p>
      </div>
      <div class="dialog-footer">
        <button 
          v-if="showCancel" 
          @click="handleCancel" 
          class="dialog-btn dialog-btn-cancel"
        >
          {{ cancelText }}
        </button>
        <button 
          @click="handleConfirm" 
          class="dialog-btn dialog-btn-confirm"
          :class="type"
        >
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: '提示'
  },
  message: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['info', 'warning', 'error', 'success'].includes(value)
  },
  closable: {
    type: Boolean,
    default: true
  },
  showCancel: {
    type: Boolean,
    default: false
  },
  confirmText: {
    type: String,
    default: '确定'
  },
  cancelText: {
    type: String,
    default: '取消'
  },
  closeOnClickOverlay: {
    type: Boolean,
    default: true
  },
  width: {
    type: String,
    default: null
  },
  customClass: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:visible', 'confirm', 'cancel', 'close'])

const close = () => {
  emit('update:visible', false)
  emit('close')
}

const handleConfirm = () => {
  emit('confirm')
  close()
}

const handleCancel = () => {
  emit('cancel')
  close()
}

const handleOverlayClick = () => {
  if (props.closeOnClickOverlay) {
    close()
  }
}

// 监听 visible 变化，处理 ESC 键
watch(() => props.visible, (newVal) => {
  if (newVal) {
    document.addEventListener('keydown', handleKeydown)
  } else {
    document.removeEventListener('keydown', handleKeydown)
  }
})

const handleKeydown = (e) => {
  if (e.key === 'Escape') {
    close()
  }
}
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.dialog {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;
}

.dialog-header {
  display: flex;
  align-items: center;
  padding: 1rem 1rem 0.5rem;
  position: relative;
  flex-shrink: 0;
  min-height: 0;
}

.dialog-icon {
  font-size: 1.5rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.dialog-icon.info {
  color: #007bff;
}

.dialog-icon.warning {
  color: #f39c12;
}

.dialog-icon.error {
  color: #e74c3c;
}

.dialog-icon.success {
  color: #27ae60;
}

.dialog-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  flex: 1;
  word-break: break-word;
  overflow-wrap: break-word;
  min-width: 0;
}

.dialog-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.dialog-close:hover {
  background: #f5f5f5;
  color: #666;
}

.dialog-body {
  padding: 1rem 1.5rem 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.dialog-message {
  margin: 0;
  color: #666;
  line-height: 1.6;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.5rem 1rem 0.5rem;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.dialog-btn {
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 60px;
}

.dialog-btn-cancel {
  background: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
}

.dialog-btn-cancel:hover {
  background: #e9ecef;
  border-color: #adb5bd;
  color: #343a40;
}

.dialog-btn-confirm {
  background: #007bff;
  color: white;
}

.dialog-btn-confirm:hover {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.dialog-btn-confirm.info {
  background: #007bff;
}

.dialog-btn-confirm.info:hover {
  background: #0056b3;
}

.dialog-btn-confirm.warning {
  background: #f39c12;
}

.dialog-btn-confirm.warning:hover {
  background: #e67e22;
}

.dialog-btn-confirm.error {
  background: #e74c3c;
}

.dialog-btn-confirm.error:hover {
  background: #c0392b;
}

.dialog-btn-confirm.success {
  background: #27ae60;
}

.dialog-btn-confirm.success:hover {
  background: #229954;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 768px) {
  .dialog {
    width: 95%;
    margin: 1rem;
  }
  
  .dialog-header {
    padding: 1rem;
  }
  
  .dialog-body {
    padding: 0.75rem 1rem;
  }
  
  .dialog-footer {
    padding: 0.75rem 1rem 1rem;
    flex-direction: column;
  }
  
  .dialog-btn {
    width: 100%;
  }
}
</style>
